// src/components/onboarding/ModalitiesScreen.js
// Screen 2 of 3 in the onboarding flow.
// Replaces the old 9-tile checkbox grid with the full ModalitySelector component.

import React, { useState } from 'react';
import ModalitySelector from '../common/ModalitySelector';

/** Skip link — span instead of button to prevent browser tooltip on hover. */
const SkipLink = ({ onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontSize: '14px',
        color: hovered ? '#6D28D9' : '#6B7280',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'color 0.15s ease',
      }}
    >
      Skip for now
    </span>
  );
};

/**
 * Parse initial modalities from Onboarding formData.
 * Handles two formats:
 *   - New: { selected: [...], custom: [...], parentCategories: [...] }
 *   - Old (pre-taxonomy): [] or array of strings — treated as empty (can't map)
 */
const parseInitialModalities = (raw) => {
  if (!raw) return { selected: [], custom: [] };
  // New structured format
  if (typeof raw === 'object' && !Array.isArray(raw) && raw.selected !== undefined) {
    return {
      selected: Array.isArray(raw.selected) ? raw.selected : [],
      custom: Array.isArray(raw.custom) ? raw.custom : [],
    };
  }
  // Old flat array format — discard (strings aren't mappable to new objects)
  return { selected: [], custom: [] };
};

const ModalitiesScreen = ({ initialData, onBack, onContinue, onSkip }) => {
  const { selected: initSelected, custom: initCustom } = parseInitialModalities(
    initialData?.modalities
  );

  const [selectedModalities, setSelectedModalities] = useState(initSelected);
  const [customModalities, setCustomModalities] = useState(initCustom);

  const handleChange = (selected, custom) => {
    setSelectedModalities(selected);
    setCustomModalities(custom);
  };

  const handleContinue = () => {
    // Auto-compute parentCategories from selected modalities
    const parentCategories = [...new Set(selectedModalities.map((m) => m.parentId))];
    onContinue({
      selected: selectedModalities,
      custom: customModalities,
      parentCategories,
    });
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="space-y-6">
      {/* Headline */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          What's Your World?
        </h1>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          What areas speak to you — or what are you already offering?
        </p>
      </div>

      {/* Modality Selector — maxHeight keeps card from growing infinitely */}
      <ModalitySelector
        selectedModalities={selectedModalities}
        customModalities={customModalities}
        onChange={handleChange}
        maxHeight="420px"
      />

      {/* Reassurance text */}
      <p className="text-xs text-center text-gray-600">
        You can always change these later — this just helps us personalize your experience.
      </p>

      {/* Navigation — constrained width, centered */}
      <div style={{ display: 'flex', gap: '12px', maxWidth: '360px', margin: '0 auto' }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            backgroundColor: 'white',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background-color 0.15s ease',
          }}
        >
          {'\u2190'} Back
        </button>
        <button
          onClick={handleContinue}
          style={{
            flex: 1,
            maxWidth: '300px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            color: 'white',
            backgroundColor: '#7c3aed',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(124,58,237,0.3)',
            transition: 'background-color 0.15s ease',
          }}
        >
          Continue {'\u2192'}
        </button>
      </div>

      {/* Skip link */}
      <div className="text-center">
        <SkipLink onClick={handleSkip} />
      </div>
    </div>
  );
};

export default ModalitiesScreen;
